import axios from 'axios';
import { getToken, removeToken } from './auth';
import { message } from 'antd';

// Get API URL dynamically based on browser URL (runtime detection)
// Always use the host from browser URL, never use environment variables
// This ensures the app works when accessed from any host
// Export this function so it can be used in other components (e.g., for fetch calls)
export const getApiBaseURL = () => {
  if (typeof window === 'undefined') {
    // Server-side rendering fallback
    return '/api';
  }
  
  // Get current host information from browser URL
  const currentHost = window.location.hostname;
  const currentPort = window.location.port;
  const currentProtocol = window.location.protocol; // 'http:' or 'https:'
  
  // Strategy:
  // 1. If accessed through Nginx (port 80/443 or no port): use relative path '/api'
  //    Nginx will proxy /api requests to backend
  // 2. If accessed directly through frontend container (port 3000): 
  //    construct backend URL using same host but port 5000
  
  if (!currentPort || currentPort === '80' || currentPort === '443') {
    // Accessing through Nginx - use relative path
    // Browser will automatically use current host
    return '/api';
  }
  
  // Direct access to frontend container (e.g., port 3000)
  // Use same host but backend port (5000)
  // This ensures cross-host access works correctly
  const backendPort = '5000';
  return `${currentProtocol}//${currentHost}:${backendPort}/api`;
};

const apiBaseURL = getApiBaseURL();

// Always log API base URL for debugging (helps troubleshoot cross-host issues)
console.log('API Base URL:', apiBaseURL);
console.log('Current location:', typeof window !== 'undefined' ? {
  origin: window.location.origin,
  hostname: window.location.hostname,
  port: window.location.port,
  protocol: window.location.protocol
} : 'N/A');

const api = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`Request to ${config.url} with token: ${token.substring(0, 20)}...`);  // 调试信息
    } else {
      console.warn('No token found in request to:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 401: 未授权，清除token并跳转登录
    // 但是，如果是 logout 请求，不要自动跳转，让 handleLogout 处理
    if (error.response?.status === 401) {
      const isLogoutRequest = error.config?.url?.includes('/logout');
      if (isLogoutRequest) {
        // logout 请求返回 401 是正常的（token 可能已过期），直接返回错误让调用方处理
        console.log('Logout request returned 401, this is expected if token is expired');
        return Promise.reject(error);
      }
      // 避免在登录页面时重复处理
      if (!window.location.pathname.includes('login')) {
        removeToken();
        const errorMsg = error.response?.data?.error || '登录已过期，请重新登录';
        // 如果是 token 被撤销（单点登录），显示更明确的提示
        if (errorMsg.includes('revoked') || errorMsg.includes('Token')) {
          message.error('您的账号在其他地方登录，请重新登录');
        } else {
          message.error(errorMsg);
        }
        // 延迟重新加载，避免循环
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      }
    } else if (error.response?.status === 422) {
      // 422: JWT验证失败，但不自动清除token，让调用方处理
      console.error('JWT Error:', error.response.data);
      const errorMsg = error.response.data?.error || 'Token验证失败';
      // 只在明确是token问题时才提示
      if (errorMsg.toLowerCase().includes('token') || errorMsg.toLowerCase().includes('authorization')) {
        // 不自动清除token，让组件层处理
        console.warn('Token validation failed, but not auto-clearing to avoid loop');
      }
    }
    return Promise.reject(error);
  }
);

export default api;

